import { Clock, Eye, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useSalonStore } from "@/lib/store/salon";

interface PreviewOption {
  optionName: string;
  priceModifier: number;
}

interface PreviewOptionGroup {
  selectionType: "SINGLE" | "MULTIPLE";
  options: PreviewOption[];
}

interface ServicePreviewCardProps {
  name: string;
  description?: string;
  duration: number;
  pricingMode: "SINGLE_PRICE" | "MATRIX_PRICE" | "NO_PRICE";
  basePrice?: number;
  optionGroups?: PreviewOptionGroup[];
  displayMode?: "TABLE" | "REGULAR" | null;
}

const CURRENCY_LOCALES: Record<string, string> = {
  USD: "en-US",
  EUR: "de-DE",
  VND: "vi-VN",
  JPY: "ja-JP",
  PLN: "pl-PL",
  CZK: "cs-CZ",
};

const formatPrice = (value: number, currency = "USD") => {
  const locale = CURRENCY_LOCALES[currency] ?? "en-US";
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
  }).format(value);
};

export function ServicePreviewCard({
  name,
  description,
  duration,
  pricingMode,
  basePrice,
  optionGroups,
  displayMode,
}: ServicePreviewCardProps) {
  const { t } = useTranslation("services");
  const currency = useSalonStore((state) => state.currentSalon?.currency) ?? "USD";

  const hasName = name.trim().length > 0;
  const isMatrix = pricingMode === "MATRIX_PRICE";
  const firstGroup = optionGroups?.[0];

  // TABLE display: only when explicitly chosen + matrix + first group is SINGLE + has options
  const tableOptions =
    isMatrix &&
    displayMode === "TABLE" &&
    firstGroup?.selectionType === "SINGLE" &&
    firstGroup.options.length > 0
      ? firstGroup.options
      : null;

  // Mirror storefront fromPrice logic: if basePrice is 0, use min option from first SINGLE group
  const fromPrice = (() => {
    const base = basePrice ?? 0;
    if (base > 0) return base;
    const firstSingle = optionGroups?.find((g) => g.selectionType === "SINGLE");
    if (firstSingle && firstSingle.options.length > 0) {
      const minMod = Math.min(...firstSingle.options.map((o) => o.priceModifier));
      return base + minMod;
    }
    return base;
  })();

  const ServiceInfo = () => (
    <>
      <div className="flex items-center gap-1.5 min-w-0">
        <p
          className={`text-lg font-semibold truncate ${
            hasName ? "text-rose-600" : "text-gray-400 italic"
          }`}
        >
          {hasName ? name : t("dialog.preview.noName")}
        </p>
      </div>
      {description && description.trim().length > 0 && (
        <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{description}</p>
      )}
      {duration > 0 && (
        <div className="flex items-center gap-1 mt-0.5">
          <Clock className="h-3 w-3 flex-shrink-0 text-gray-400" />
          <span className="text-xs text-gray-500">
            {t("dialog.preview.minutes", { count: duration })}
          </span>
        </div>
      )}
    </>
  );

  return (
    <div className="rounded-lg border bg-muted/30 overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2 border-b bg-muted">
        <Eye className="w-3.5 h-3.5 text-muted-foreground" />
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          {t("dialog.preview.title")}
        </span>
      </div>

      <div className="bg-background">
        {tableOptions ? (
          <>
            {/* Column header row: empty space + option names */}
            <div className="flex border-b border-gray-100 bg-muted">
              <div className="flex-1 px-3 py-1" />
              {tableOptions.map((opt, idx) => (
                <div
                  key={`${opt.optionName}-${idx}`}
                  className="w-16 sm:w-20 border-l border-gray-100 py-1 text-center"
                >
                  <span className="text-xs font-semibold text-gray-500 truncate block px-1">
                    {opt.optionName || "…"}
                  </span>
                </div>
              ))}
            </div>

            {/* Service row */}
            <div className="flex items-stretch">
              <div className="flex-1 min-w-0 px-3 py-2.5">
                <ServiceInfo />
              </div>
              {tableOptions.map((opt, idx) => {
                const price = (basePrice ?? 0) + opt.priceModifier;
                return (
                  <div
                    key={`${opt.optionName}-price-${idx}`}
                    className="w-16 sm:w-20 border-l border-gray-100 flex items-center justify-center text-center"
                  >
                    <span className="text-xs font-semibold text-rose-600">
                      {formatPrice(price, currency)}
                    </span>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div className="px-4 py-3 space-y-2">
            <div className="flex min-h-[68px] w-full items-center justify-between">
              <div className="min-w-0 flex-1 pr-2.5">
                <ServiceInfo />
              </div>
              <div className="flex flex-shrink-0 items-center gap-1.5">
                {pricingMode === "NO_PRICE" ? (
                  <span className="rounded-md bg-amber-50 px-2 py-1 text-xs font-semibold text-amber-700 inline-flex items-center gap-1">
                    <Sparkles className="h-3 w-3" />
                    {t("dialog.preview.quoteOnRequest")}
                  </span>
                ) : (
                  <span className="text-base font-semibold text-rose-600">
                    {isMatrix ? (
                      <>
                        <span className="text-xs font-normal text-rose-400 mr-1.5">
                          {t("dialog.preview.fromLabel")}
                        </span>
                        {formatPrice(fromPrice, currency)}
                      </>
                    ) : (
                      formatPrice(basePrice ?? 0, currency)
                    )}
                  </span>
                )}
              </div>
            </div>

            {/* REGULAR mode: show options stacked, one per row */}
            {isMatrix &&
              displayMode === "REGULAR" &&
              firstGroup &&
              firstGroup.options.length > 0 && (
                <div className="flex flex-col divide-y divide-border pt-1 border-t border-border">
                  {firstGroup.options.map((opt, idx) => {
                    const optPrice = (basePrice ?? 0) + opt.priceModifier;
                    return (
                      <div
                        key={`${opt.optionName}-${idx}`}
                        className="flex items-center justify-between py-1.5 text-sm"
                      >
                        <span className="text-foreground font-medium truncate pr-3">
                          {opt.optionName || "…"}
                        </span>
                        <span className="font-semibold text-rose-600 dark:text-rose-400 flex-shrink-0">
                          {formatPrice(optPrice, currency)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
          </div>
        )}
      </div>
    </div>
  );
}
